import { useQuery } from '@apollo/client/react'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Pannellum } from 'pannellum-react'
import { useRef, useState } from 'react'
import ReactGA from 'react-ga4'
import { LIST_PHOTOSPHEREPHOTOS } from '../api/listPhotospherePhotos'
import { scrollToId } from '../theme/utils'
import type {
  HotspotProps,
  PhotospherePhotosQuery,
  SceneMap,
  SceneProps,
  SceneWithKey,
} from '../types/tour'
import { Spinner } from './Spinner'

export default function PanolensTour() {
  const [tourLoaded, setTourLoaded] = useState(false)
  const [currentScene, setCurrentScene] = useState('centre')
  const [yaw, setYaw] = useState(0)
  const [pitch, setPitch] = useState(0)
  const panImage = useRef<Pannellum | null>(null)
  const { loading, error, data } = useQuery<PhotospherePhotosQuery>(
    LIST_PHOTOSPHEREPHOTOS
  )
  if (loading) {
    return (
      <div className="py-12 text-center">
        <Spinner size="md" />
      </div>
    )
  }
  if (error) {
    return (
      <div
        className="relative rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700"
        role="alert"
      >
        <strong className="font-bold">Oh dang!</strong>
        <span className="block sm:inline">
          There was an error loading the virtual tour.
        </span>
        <span className="absolute bottom-0 right-0 top-0 px-4 py-3">
          <title>Close</title>
          <FontAwesomeIcon icon={faXmark} />
        </span>
      </div>
    )
  }
  if (!data) {
    return null // or a fallback UI
  }
  const scenesArray: SceneMap = data.listPhotospherePhotos.data
    .map((scene: SceneProps) => {
      const sceneItem: SceneWithKey = {
        id: scene.id,
        key: scene.id,
        slug: scene.slug,
        title: scene.title,
        image: scene.image,
        initialPitch: scene.initialPitch,
        initialYaw: scene.initialYaw,
        hotspots: scene.hotspots.map((hotspot: HotspotProps) => {
          const hotspotItem: HotspotProps = {
            id: hotspot.id,
            pitch: hotspot.pitch,
            yaw: hotspot.yaw,
            text: hotspot.text,
            type: hotspot.type,
            transition: hotspot.transition,
            url: hotspot.url,
          }
          return hotspotItem
        }),
      }
      return { [scene.slug]: sceneItem }
    })
    .reduce<SceneMap>((acc, cur) => {
      return { ...acc, ...cur }
    }, {})

  return (
    <section id="tour" className="container-responsive cv-auto py-16 sm:py-24">
      <div className="mb-8 max-w-3xl">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          The space
        </h2>
        <p className="mt-3 text-neutral-600 dark:text-neutral-300">
          Explore the main sound stage and facilities in an interactive 360°
          tour, and get a quick overview of what the space offers for recording,
          rehearsal, and long-term rentals.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <aside className="order-2 lg:order-1 lg:col-span-2">
          <div className="sticky top-16 overflow-hidden rounded-xl border border-neutral-200 bg-black/95 shadow-sm dark:border-neutral-800">
            <Pannellum
              ref={panImage}
              key={scenesArray[currentScene].key}
              title={tourLoaded ? scenesArray[currentScene].title : ''}
              preview={'../../media/hero/strangeways_2025_640.webp'}
              width="100%"
              height="500px"
              image={scenesArray[currentScene].image}
              pitch={scenesArray[currentScene].initialPitch}
              yaw={scenesArray[currentScene].initialYaw}
              hfov={100}
              maxHfov={120}
              minHfov={80}
              haov={360}
              vaov={180}
              autoRotate={1}
              autoLoad={tourLoaded}
              keyboardZoom={false}
              mouseZoom={true}
              showZoomCtrl={true}
              showFullscreenCtrl={true}
              showControls={tourLoaded}
              onLoad={() => {
                setTourLoaded(true)
                ReactGA.event({
                  category: 'Button Click',
                  action: `User loaded the virtual tour`,
                  label: 'Tour Section',
                })
              }}
              onError={(err: Error) => {
                console.debug('Error', err)
              }}
              onErrorcleared={() => {
                console.debug('Error Cleared')
              }}
              onMouseup={(event: MouseEvent) => {
                console.debug('Mouse Up', event)
                if (panImage.current !== null) {
                  setPitch(
                    panImage.current.getViewer().mouseEventToCoords(event)[0]
                  )
                  setYaw(
                    panImage.current.getViewer().mouseEventToCoords(event)[1]
                  )
                }
              }}
              onMousedown={() => {
                console.debug('Pitch', pitch, 'Yaw', yaw)
              }}
              getViewer={function (): {
                mouseEventToCoords: (event: MouseEvent) => number[]
              } {
                throw new Error('Function not implemented.')
              }}
            >
              {scenesArray[currentScene].hotspots.map(
                (hotspot: HotspotProps) => {
                  return (
                    <Pannellum.Hotspot
                      key={hotspot.id}
                      pitch={hotspot.pitch}
                      yaw={hotspot.yaw}
                      type={hotspot.type}
                      text={hotspot.text}
                      handleClick={() => {
                        setCurrentScene(hotspot.transition.slug)
                        ReactGA.event({
                          category: 'Button Click',
                          action: `User navigated to: ${scenesArray[currentScene].title}`,
                          label: 'Tour Section',
                        })
                      }}
                    />
                  )
                }
              )}
            </Pannellum>
          </div>
        </aside>

        <div className="order-1 lg:order-2 lg:col-span-1">
          <div className="space-y-6 rounded-xl border border-neutral-300 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-900">
            <header>
              <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-white">
                The nitty gritty
              </h3>
              <p className="text-sm mt-3 leading-relaxed text-neutral-600 dark:text-neutral-300">
                A flexible, acoustically controlled studio complex centred
                around a large sound stage, with smaller writing and post rooms.
              </p>
            </header>

            <section
              aria-labelledby="studio-a-heading"
              className="border-t border-neutral-200 pt-5 dark:border-neutral-700"
            >
              <h4
                id="studio-a-heading"
                className="text-xs font-bold uppercase tracking-wider text-neutral-800 dark:text-neutral-100"
              >
                Studio A – main sound stage
              </h4>
              <p className="text-sm mt-2 leading-relaxed text-neutral-700 dark:text-neutral-200">
                25&apos; × 25&apos; live room with 20&apos; ceilings and
                acoustic clouds at 16&apos;. Fully sound-proof and
                professionally treated.
              </p>
              <ul className="text-sm mt-3 space-y-1 text-neutral-600 dark:text-neutral-300">
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  Mixture of rugs, wood, brick and fabric surfaces
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  Bass traps and overhead clouds for controlled ambience
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  Ideal for full-band recording, rehearsal and live sessions
                </li>
              </ul>
            </section>

            <section
              aria-labelledby="studios-bc-heading"
              className="border-t border-neutral-200 pt-5 dark:border-neutral-700"
            >
              <h4
                id="studios-bc-heading"
                className="text-xs font-bold uppercase tracking-wider text-neutral-800 dark:text-neutral-100"
              >
                Studios B &amp; C – writing &amp; post rooms
              </h4>
              <p className="text-sm mt-2 leading-relaxed text-neutral-700 dark:text-neutral-200">
                Two smaller rooms available on a month-to-month basis.
              </p>
              <ul className="text-sm mt-3 space-y-1 text-neutral-600 dark:text-neutral-300">
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  <span>
                    <strong className="font-semibold text-neutral-800 dark:text-neutral-100">
                      Studio B
                    </strong>{' '}
                    – tightly controlled, ideal for vocal production, VO and
                    ADR.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  <span>
                    <strong className="font-semibold text-neutral-800 dark:text-neutral-100">
                      Studio C
                    </strong>{' '}
                    – woody, lively room tailored for strings and acoustic
                    instruments (currently rented).
                  </span>
                </li>
              </ul>
              <p className="text-sm mt-3 text-neutral-600 dark:text-neutral-300">
                Month-to-month studio rentals from{' '}
                <span className="font-semibold text-neutral-900 dark:text-white">
                  $800 / month
                </span>
                .
              </p>
            </section>

            <section
              aria-labelledby="amenities-heading"
              className="border-t border-neutral-200 pt-5 dark:border-neutral-700"
            >
              <h4
                id="amenities-heading"
                className="text-xs font-bold uppercase tracking-wider text-neutral-800 dark:text-neutral-100"
              >
                Amenities
              </h4>
              <ul className="text-sm mt-3 space-y-1 text-neutral-600 dark:text-neutral-300">
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  Kitchenette with microwave, hotplate, convection oven
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  Mini fridge and coffee machine
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  Washroom and small patio out back
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  Large load-in door directly into Studio A
                </li>
              </ul>
            </section>

            <section
              aria-labelledby="access-heading"
              className="border-t border-neutral-200 pt-5 dark:border-neutral-700"
            >
              <h4
                id="access-heading"
                className="text-xs font-bold uppercase tracking-wider text-neutral-800 dark:text-neutral-100"
              >
                Access, parking &amp; accessibility
              </h4>
              <ul className="text-sm mt-3 space-y-1 text-neutral-600 dark:text-neutral-300">
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  Ample on-site parking with 24-hour security
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  Easy access for trucks and large equipment
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400 dark:bg-neutral-500" />
                  Wheelchair accessible facility
                </li>
              </ul>
            </section>

            <footer className="border-t border-neutral-200 pt-5 dark:border-neutral-700">
              <p className="text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
                Interested in a long-term room or a specific setup?{' '}
                <a
                  href="#contact"
                  className="hyperlink font-semibold text-neutral-900 underline decoration-neutral-400 underline-offset-2 transition-colors hover:text-neutral-700 hover:decoration-neutral-600 dark:text-white dark:decoration-neutral-500 dark:hover:text-neutral-200 dark:hover:decoration-neutral-300"
                  onClick={(e) => {
                    e.preventDefault()
                    scrollToId('contact')
                  }}
                >
                  Get in touch
                </a>{' '}
                to talk through availability and build-out options.
              </p>
            </footer>
          </div>
        </div>
      </div>
    </section>
  )
}
